import { createBlock } from './blockUtils';
import { createChoiceBlock } from './choiceUtils';
import { focusEditable, focusLinkedVariableRowPicker } from './focusUtils';
import { createLocalImageBlock, pickDocImageFile } from './imageFiles';
import { startDocImageUpload } from './docImageUploadBridge';
import { createVideoBlock } from './videoFiles';

/**
 * Apply a slash-menu command: convert the current block (and/or insert new
 * ones) according to the chosen block type, then move focus somewhere useful.
 *
 * `beforeSlash` is whatever the user had typed before the "/" — it is kept
 * as the converted block's content so no text is lost.
 */
export const applySlashCommand = ({
  commandId,
  block,
  beforeSlash,
  onUpdate,
  onUpdateAndInsert,
  hasVariablesBlock = false,
}) => {
  if (commandId.startsWith('heading-')) {
    const level = parseInt(commandId.split('-')[1], 10);
    onUpdate(block.id, {
      type: 'heading',
      content: beforeSlash,
      props: { ...block.props, level },
    });
    focusEditable(block.id);
    return;
  }

  switch (commandId) {
    case 'checkbox': {
      // Note: block.id is the stable id the per-mission run-state will key on
      // later (Chunk 11b). "checked" here is template-editing state only.
      onUpdate(block.id, {
        type: 'checkbox',
        content: beforeSlash,
        props: { ...block.props, checked: false },
      });
      focusEditable(block.id);
      return;
    }

    case 'bullet': {
      onUpdate(block.id, {
        type: 'bullet',
        content: beforeSlash,
        props: { ...block.props },
      });
      focusEditable(block.id);
      return;
    }

    case 'number': {
      onUpdate(block.id, {
        type: 'number',
        content: beforeSlash,
        props: { ...block.props },
      });
      focusEditable(block.id);
      return;
    }

    case 'choice': {
      const choice = createChoiceBlock(beforeSlash);
      onUpdate(block.id, {
        type: choice.type,
        content: choice.content,
        props: choice.props,
        children: choice.children,
      });
      focusEditable(block.id);
      return;
    }

    case 'callout': {
      const child = createBlock('text', beforeSlash);
      onUpdate(block.id, {
        type: 'callout',
        content: '',
        props: { color: 'neutral' },
        children: [child],
      });
      focusEditable(child.id);
      return;
    }

    case 'quote': {
      const child = createBlock('text', beforeSlash);
      onUpdate(block.id, {
        type: 'callout',
        content: '',
        props: { icon: '❝', variant: 'quote' },
        children: [child],
      });
      focusEditable(child.id);
      return;
    }

    case 'toggle': {
      // Text before the "/" becomes the toggle's title; starts open with one
      // empty paragraph inside so there's somewhere to type.
      const child = createBlock('text');
      onUpdate(block.id, {
        type: 'toggle',
        content: beforeSlash,
        props: { isOpen: true },
        children: [child],
      });
      focusEditable(child.id);
      return;
    }

    case 'code': {
      // Text typed before the "/" becomes the first line of code. Focus the
      // code contenteditable (also has .doc-editable for focusEditable).
      onUpdate(block.id, {
        type: 'code',
        content: beforeSlash,
        props: { language: 'plaintext', wrapText: true, showLineNumbers: false },
      });
      setTimeout(() => {
        document
          .querySelector(
            `[data-block-id="${block.id}"] .code-block-editable, [data-code-block="${block.id}"]`
          )
          ?.focus();
      }, 0);
      return;
    }

    case 'table': {
      // 3×2 starter grid; any text typed before the "/" seeds the first
      // header cell. Focus lands there (data-table-cell, not focusEditable —
      // table cells aren't standard block editables).
      onUpdate(block.id, {
        type: 'table',
        content: '',
        props: {
          tableData: {
            headers: [beforeSlash || 'Column 1', 'Column 2', 'Column 3'],
            rows: [
              ['', '', ''],
              ['', '', ''],
            ],
          },
        },
      });
      setTimeout(() => {
        document.querySelector(`[data-table-cell="${block.id}--1-0"]`)?.focus();
      }, 0);
      return;
    }

    case 'variables': {
      // One variables table per doc — slash is hidden when one exists; guard
      // here too in case a stale menu fires.
      if (hasVariablesBlock) {
        onUpdate(block.id, { type: 'text', content: beforeSlash, props: {} });
        focusEditable(block.id);
        return;
      }
      // Atomic block — keep any text before "/" by inserting a trailing text
      // block (same pattern as divider).
      onUpdateAndInsert(
        block.id,
        { type: 'variables', content: '', props: {} },
        createBlock('text', beforeSlash || '')
      );
      return;
    }

    case 'linked-variable-row': {
      // Atomic — as many as you want; each binds to one existing variable.
      // Don't park the caret in the trailing paragraph — the picker search
      // is the next thing the user needs to type in.
      onUpdateAndInsert(
        block.id,
        {
          type: 'linked-variable-row',
          content: '',
          props: { variableId: null, variableName: '' },
        },
        createBlock('text', beforeSlash || ''),
        { focusNewBlock: false }
      );
      focusLinkedVariableRowPicker(block.id);
      return;
    }

    case 'divider': {
      // One atomic tree change — a second onChange from the same snapshot
      // would clobber the conversion
      onUpdateAndInsert(
        block.id,
        { type: 'divider', content: '', props: {} },
        createBlock('text')
      );
      return;
    }

    case 'file-upload': {
      onUpdateAndInsert(
        block.id,
        { type: 'file-upload', content: '', props: {} },
        createBlock('text', beforeSlash || '')
      );
      return;
    }

    case 'snippet': {
      // Atomic citation card — picker opens from the empty block UI
      onUpdateAndInsert(
        block.id,
        {
          type: 'snippet',
          content: '',
          props: {
            sourceDocId: null,
            sourceBlockId: null,
            sourceBlockIds: [],
          },
        },
        createBlock('text', beforeSlash || '')
      );
      return;
    }

    case 'image': {
      // File picker first; convert the slash block once a file is chosen
      // (or leave the text block alone if the user cancels).
      pickDocImageFile().then((file) => {
        if (!file) {
          focusEditable(block.id);
          return;
        }
        const imageBlock = createLocalImageBlock(file);
        onUpdateAndInsert(
          block.id,
          {
            type: imageBlock.type,
            content: imageBlock.content,
            props: imageBlock.props,
            children: imageBlock.children,
          },
          createBlock('text')
        );
        // onUpdateAndInsert keeps the converted block's id as `block.id`
        if (imageBlock.props?.uploadStatus === 'local') {
          startDocImageUpload(block.id, file);
        }
      });
      return;
    }

    case 'video': {
      // Chooser: embed link vs upload (upload is a frontend stub for now)
      const video = createVideoBlock({ mode: 'chooser' });
      onUpdateAndInsert(
        block.id,
        {
          type: video.type,
          content: video.content,
          props: video.props,
          children: video.children,
        },
        createBlock('text', beforeSlash || '')
      );
      return;
    }

    case 'youtube':
    case 'vimeo':
    case 'loom': {
      // Provider shortcuts skip the chooser and open the URL prompt
      const video = createVideoBlock({ mode: 'embed', provider: commandId });
      onUpdateAndInsert(
        block.id,
        {
          type: video.type,
          content: video.content,
          props: video.props,
          children: video.children,
        },
        createBlock('text', beforeSlash || '')
      );
      return;
    }

    case 'row': {
      // Rows hold columns; columns hold vertically stacked blocks
      const first = createBlock('text', beforeSlash);
      const leftColumn = { ...createBlock('column'), children: [first] };
      const rightColumn = { ...createBlock('column'), children: [createBlock('text')] };
      onUpdate(block.id, {
        type: 'row',
        content: '',
        props: {},
        children: [leftColumn, rightColumn],
      });
      focusEditable(first.id);
      return;
    }

    case 'text':
    default: {
      onUpdate(block.id, { type: 'text', content: beforeSlash, props: {} });
      focusEditable(block.id);
    }
  }
};
